import type { JSX } from 'react';
import Ai21Labs from '@gonfalon/img/icons/ai-model-families/ai21-labs.svg';
import AnthropicDark from '@gonfalon/img/icons/ai-model-families/anthropic-dark.svg';
import AnthropicLight from '@gonfalon/img/icons/ai-model-families/anthropic-light.svg';
import AwsDark from '@gonfalon/img/icons/ai-model-families/aws-dark.svg';
import AwsLight from '@gonfalon/img/icons/ai-model-families/aws-light.svg';
import Azure from '@gonfalon/img/icons/ai-model-families/azure.svg';
import Cohere from '@gonfalon/img/icons/ai-model-families/cohere.svg';
import Cursor from '@gonfalon/img/icons/ai-model-families/cursor.svg';
import Databricks from '@gonfalon/img/icons/ai-model-families/databricks.svg';
import DeepSeek from '@gonfalon/img/icons/ai-model-families/deepseek.svg';
import FireworksAI from '@gonfalon/img/icons/ai-model-families/fireworks.svg';
import Gemini from '@gonfalon/img/icons/ai-model-families/gemini.svg';
import Meta from '@gonfalon/img/icons/ai-model-families/meta.svg';
import Mistral from '@gonfalon/img/icons/ai-model-families/mistral.svg';
import OpenAiDark from '@gonfalon/img/icons/ai-model-families/openai-dark.svg';
import OpenAiLight from '@gonfalon/img/icons/ai-model-families/openai-light.svg';
import Perplexity from '@gonfalon/img/icons/ai-model-families/perplexity.svg';
import Snowflake from '@gonfalon/img/icons/ai-model-families/snowflake.svg';
import TypeSafe from '@gonfalon/img/icons/ai-model-families/typesafe.svg';
import Vertex from '@gonfalon/img/icons/ai-model-families/vertex.svg';
import WatsonDark from '@gonfalon/img/icons/ai-model-families/watson-dark.svg';
import WatsonLight from '@gonfalon/img/icons/ai-model-families/watson-light.svg';
import type { Theme } from '@gonfalon/theme/types';
import { useThemeValue } from '@gonfalon/theme/useThemeValue';
import { clsx } from 'clsx';

import type { BedrockVariant } from '#components/AiConfigs/AiConfigModelConfiguration/modelSchemas/Bedrock';

import type { ModelProvider } from '../AiConfigModelConfiguration/modelSchemas/aiConfigModelService';

import styles from './ModelIcon.module.css';

const modelFamilyImages: Record<ModelProvider | '', { default: JSX.Element; dark: JSX.Element } | null> = {
  'AI21 Labs': {
    default: <Ai21Labs />,
    dark: <Ai21Labs />,
  },
  Anthropic: {
    default: <AnthropicLight />,
    dark: <AnthropicDark />,
  },
  Bedrock: {
    default: <AwsLight />,
    dark: <AwsDark />,
  },
  Cortex: {
    default: <Snowflake />,
    dark: <Snowflake />,
  },
  Cursor: {
    default: <Cursor />,
    dark: <Cursor />,
  },
  Databricks: {
    default: <Databricks />,
    dark: <Databricks />,
  },
  'Fireworks AI': {
    default: <FireworksAI />,
    dark: <FireworksAI />,
  },
  Cohere: {
    default: <Cohere />,
    dark: <Cohere />,
  },
  Gemini: {
    default: <Gemini />,
    dark: <Gemini />,
  },
  Meta: {
    default: <Meta />,
    dark: <Meta />,
  },
  Mistral: {
    default: <Mistral />,
    dark: <Mistral />,
  },
  OpenAI: {
    default: <OpenAiLight />,
    dark: <OpenAiDark />,
  },
  Perplexity: {
    default: <Perplexity />,
    dark: <Perplexity />,
  },
  TypeSafe: {
    default: <TypeSafe />,
    dark: <TypeSafe />,
  },
  Vertex: {
    default: <Vertex />,
    dark: <Vertex />,
  },
  Azure: {
    default: <Azure />,
    dark: <Azure />,
  },
  DeepSeek: {
    default: <DeepSeek />,
    dark: <DeepSeek />,
  },
  'IBM Watson': {
    default: <WatsonLight />,
    dark: <WatsonDark />,
  },
  // Default value for when the model family is not found
  '': null,
} as const;

const getModelFamilyImage = (modelFamily: ModelProvider | '', theme: Theme) => {
  if (!modelFamilyImages[modelFamily]) {
    return null;
  }

  return modelFamilyImages[modelFamily][theme];
};
// Models that don't match the standard model family naming conventions of various vendors
const outlierModels: Record<string, ModelProvider> = {
  'tts-1': 'OpenAI',
  'text-embedding-3-large': 'OpenAI',
  'text-embedding-3-small': 'OpenAI',
  'text-embedding-ada-002': 'OpenAI',
  'omni-moderation-latest': 'OpenAI',
  'omni-moderation-2024-09-26': 'OpenAI',
  'text-moderation-latest': 'OpenAI',
  'text-moderation-stable': 'OpenAI',
  'text-moderation-007': 'OpenAI',

  'textembedding-gecko@001': 'Vertex',
  'textembedding-gecko@002': 'Vertex',
  'textembedding-gecko@003': 'Vertex',
  'text-embedding-004': 'Vertex',
  'textembedding-gecko-multilingual@001': 'Vertex',
  'text-multilingual-embedding-002': 'Vertex',
  multimodalembedding: 'Vertex',
  'medlm-medium': 'Vertex',
  'medlm-large': 'Vertex',
};

export const getModelFamilyFromModelName = (modelName?: string, provider?: string): ModelProvider | '' => {
  // TODO: This is a temporary fix to handle the Cortex provider. We will need to improve this logic to handle identical model names from other providers.
  // For example, llame, claude, and mistral are all models provided by Cortex.
  // We should simplify this logic once we ensure provider is always defined
  if (provider === 'Cortex') {
    return 'Cortex';
  }

  if (provider === 'Cursor') {
    return 'Cursor';
  }

  if (provider === 'TypeSafe') {
    return 'TypeSafe';
  }

  if (!modelName) {
    return '';
  }

  // Bedrock models all follow a similar pattern:
  // anthropic.claude-3-5-haiku-20241022-v1:0
  // meta.llama2-70b-chat-v1
  // mistral.pixtral-large-2502-v1:0
  // etc.
  // So the following regex matches, in non-regex, as:
  // 1. Match a string starting with 1-10 characters
  // 2. Followed by a period
  // 3. Followed by any number of characters or digits
  // 4. Followed by the letter v
  // 5. Followed by 1 digit
  // In a practical example, this would match:
  // 1. (anthropic)
  // 2. (.)
  // 3. (claude-3-5-haiku-20241022-)
  // 4. (v)
  // 5. (1)
  // As long as they conform to this pattern, they should all get picked up as bedrock-provided models
  if (/\w{0,10}\.[\w\d].+v\d/.test(modelName)) {
    return 'Bedrock';
  }

  if (/(anthropic|claude|opus|sonnet)/.test(modelName)) {
    if (modelName.includes('anthropic.')) {
      return 'Bedrock';
    }

    if (modelName.includes('@')) {
      return 'Vertex';
    }

    return 'Anthropic';
  }

  if (/granite|watson/.test(modelName)) {
    return 'IBM Watson';
  }

  if (/(openai|gpt|dall-e|o1-|o3-|o4-)/.test(modelName)) {
    return 'OpenAI';
  }

  if (/(gemini)/.test(modelName)) {
    return 'Gemini';
  }

  if (/(fireworks)/.test(modelName)) {
    return 'Fireworks AI';
  }

  if (/(meta|llama)/.test(modelName)) {
    return 'Meta';
  }

  if (/(mistral|codestral)/.test(modelName)) {
    return 'Mistral';
  }

  if (/(jamba|ai21)/.test(modelName)) {
    return 'AI21 Labs';
  }

  if (/(amazon|titan|nova)/.test(modelName)) {
    return 'Bedrock';
  }

  if (/(cohere)/.test(modelName)) {
    return 'Cohere';
  }

  if (/(deepseek)/.test(modelName)) {
    return 'DeepSeek';
  }

  if (/(sonar|perplexity|r1-1776)/.test(modelName)) {
    return 'Perplexity';
  }

  if (/^jev-|typesafe/.test(modelName)) {
    return 'TypeSafe';
  }

  if (/(databricks|dbrx)/.test(modelName)) {
    return 'Databricks';
  }

  if (/(composer)/.test(modelName)) {
    return 'Cursor';
  }

  return outlierModels[modelName] || '';
};

type ModelIconProps = {
  modelName: string;
  provider?: ModelProvider | BedrockVariant;
  className?: string;
};

export const ModelIcon = ({ modelName, provider, className = '' }: ModelIconProps) => {
  const theme = useThemeValue();

  if (provider) {
    return <ModelIconProvider provider={provider} className={className} />;
  }

  return (
    <div className={clsx(styles.ModelIcon, className)} data-test-id={getModelFamilyFromModelName(modelName, provider)}>
      {getModelFamilyImage(getModelFamilyFromModelName(modelName, provider), theme)}
    </div>
  );
};

type ModelIconProviderProps = {
  provider?: ModelProvider | BedrockVariant;
  className?: string;
};

export const ModelIconProvider = ({ provider, className = '' }: ModelIconProviderProps) => {
  const theme = useThemeValue();

  if (!provider) {
    return null;
  }

  // For Bedrock variants like "Bedrock:Anthropic", prefer the sub-provider icon
  // (e.g. Anthropic) when available, falling back to the base provider (Bedrock).
  const parts = provider.split(':');
  const subProvider = parts[1] as ModelProvider | undefined;
  const baseProvider = parts[0] as ModelProvider;
  const iconProvider = subProvider && modelFamilyImages[subProvider] ? subProvider : baseProvider;

  return (
    <div className={clsx(styles.ModelIcon, className)} data-test-id={provider}>
      {getModelFamilyImage(iconProvider, theme)}
    </div>
  );
};
